import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, LayoutTemplate, ShoppingBag, UtensilsCrossed, Tag, Star, Settings, BarChart3, LogOut, Menu as MenuIcon, Store, Users, CreditCard, MessageSquare, Bell, ScanLine } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import NewOrderAlerts, { AlertSoundControl, ChatSoundControl } from "@/pages/admin/NewOrderAlerts";
import { enablePush, pushSupported } from "@/lib/push";
import { toast } from "sonner";

const LINKS = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", Icon: ShoppingBag },
  { to: "/admin/chat", label: "Chat", Icon: MessageSquare },
  { to: "/admin/menu", label: "Menu", Icon: UtensilsCrossed },
  { to: "/admin/offers", label: "Offers & Promos", Icon: Tag },
  { to: "/admin/reviews", label: "Reviews", Icon: Star },
  { to: "/admin/staff", label: "Staff", Icon: Users },
  { to: "/admin/customers", label: "Customers", Icon: Users },
  { to: "/admin/scanner", label: "Scan / Walk-in", Icon: ScanLine },
  { to: "/admin/payouts", label: "Payouts", Icon: CreditCard },
  { to: "/admin/analytics", label: "Analytics", Icon: BarChart3 },
  { to: "/admin/settings", label: "CMS & Settings", Icon: Settings },
  { to: "/admin/homepage", label: "Homepage Builder", Icon: LayoutTemplate, superOnly: true },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    if (!loading && (!user || !["admin", "branch_admin", "super_admin"].includes(user.role))) navigate("/login", { state: { from: "/admin" } });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user || !["admin", "branch_admin", "super_admin"].includes(user.role)) return;
    let active = true;
    const poll = async () => {
      try { const { data } = await api.get("/admin/chat/threads"); if (active) setChatUnread((data || []).reduce((s, t) => s + (t.unread || 0), 0)); } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  if (loading || !user) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-metros-red" size={32} /></div>;
  if (!["admin", "branch_admin", "super_admin"].includes(user.role)) return null;

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <aside data-testid="admin-sidebar" className="hidden md:flex flex-col fixed left-0 top-0 h-screen w-[260px] bg-metros-black text-white grain overflow-y-auto">
        <div className="p-6 border-b border-white/10 shrink-0"><Logo dark /><p className="text-xs text-gray-500 mt-1">Admin Dashboard</p></div>
        <nav className="flex-1 p-4 space-y-1 shrink-0">
          {LINKS.filter((l) => !l.superOnly || user.role === "super_admin").map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-metros-red text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon size={18} /> {label}
              {label === "Chat" && chatUnread > 0 && (
                <span data-testid="admin-chat-unread-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-metros-red text-white text-[11px] font-bold flex items-center justify-center animate-pulse">{chatUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1 shrink-0">
          <AlertSoundControl />
          <ChatSoundControl />
          {pushSupported() && (
            <button data-testid="staff-enable-push" onClick={async () => { try { await enablePush(); toast.success("Push alerts enabled — you'll be notified even when this screen is closed"); } catch (e) { toast.error(e.message || "Could not enable push"); } }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/5"><Bell size={18} /> Enable push alerts</button>
          )}
          <a href="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/5"><Store size={18} /> View Site</a>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/5"><LogOut size={18} /> Sign Out</button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 bg-metros-black text-white flex items-center justify-between px-4 h-14">
        <Logo dark size="text-xl" />
        <button onClick={() => { logout(); navigate("/"); }} className="text-gray-300"><LogOut size={18} /></button>
      </div>
      <div className="md:hidden bg-metros-black overflow-x-auto no-scrollbar flex gap-1 px-3 py-2">
        {LINKS.filter((l) => !l.superOnly || user.role === "super_admin").map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `relative flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${isActive ? "bg-metros-red text-white" : "text-gray-400"}`}>
            <Icon size={14} /> {label}
            {label === "Chat" && chatUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-metros-red text-white text-[10px] font-bold flex items-center justify-center">{chatUnread}</span>
            )}
          </NavLink>
        ))}
      </div>

      <main className="md:ml-[260px] p-4 md:p-8">
        <Outlet />
      </main>
      <NewOrderAlerts />
    </div>
  );
}
